import { useState } from 'react';
import { useNavigate } from 'react-router';
import { AuthenticationError, trackError } from '#api/errors.ts';
import { useSuspenseTOTPAuthenticatorQuery } from '#api/totp-authenticators.queries.ts';
import { Code } from '#components/atoms/code/index.ts';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { ConfirmDialog } from '#components/patterns/confirmations/confirm-dialog/ConfirmDialog.tsx';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { useDeleteTOTPMutation } from './useDeleteTOTPMutation.ts';

type Props = {
  id: string;
  onClose: () => void;
};

export function DeleteTOTPDialog({ id, onClose }: Props) {
  const [validationError, setValidationError] = useState<string | null>(null);
  const [globalErrorMessage, setGlobalErrorMessage] = useState<string | undefined>();
  const navigate = useNavigate();
  const location = useAppLocation();
  const { mutate, isPending } = useDeleteTOTPMutation({
    onSuccess() {
      closeModal();
    },
    onError: (err) => {
      if (err instanceof AuthenticationError && err.code === 'ERR_2FA_RENEWAL_REQUIRED') {
        void navigate('/auth/mfa/renew', { state: { from: location } });
      } else {
        trackError(err);
        setGlobalErrorMessage('There was a problem deleting the authenticator.  Please try again or contact support.');
      }
    },
  });
  const totp = useSuspenseTOTPAuthenticatorQuery(id).data.data;

  // Clears out the validation error message, in case the modal is re-opened.
  function closeModal() {
    setValidationError(null);
    onClose();
  }

  return (
    <ConfirmDialog
      title={<div className="truncate">Delete {totp.name || 'authenticator'}?</div>}
      disableAccept={isPending || !totp}
      acceptButtonText="Delete"
      onCancel={closeModal}
      onAccept={() => {
        mutate(totp.id);
      }}
      shouldRestoreFocusViaFocusScope={true}
    >
      {globalErrorMessage ?
        <ErrorMessage>{globalErrorMessage}</ErrorMessage>
      : <>
          <p className="p mt-0">
            You will no longer be able to log in using this authenticator. Please ensure you have another that works.
          </p>

          <p className="p mb-0">
            Are you sure you want to delete{' '}
            {totp.name ?
              <Code className="-ml-6 wrap-anywhere">{totp.name}</Code>
            : 'this authenticator'}
            ?
          </p>

          {validationError && <ErrorMessage className="mt-32">{validationError}</ErrorMessage>}
        </>
      }
    </ConfirmDialog>
  );
}
